function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Ember from 'ember';
import Service from '@ember/service';
import { assert } from '@ember/debug';
import { registerWaiter } from '@ember/test';
import { run } from '@ember/runloop';
var MarketToastVariant = /*#__PURE__*/function (MarketToastVariant) {
  MarketToastVariant["Critical"] = "critical";
  MarketToastVariant["Info"] = "info";
  MarketToastVariant["Success"] = "success";
  MarketToastVariant["Warning"] = "warning";
  return MarketToastVariant;
}(MarketToastVariant || {});
/**
 * Convenience service for displaying market toasts in an Ember app.
 *
 * `marketToasterSelector` can be configured by apps to specify which `market-toaster` to use to show the toast.
 *
 * `dismissText` should be overridden by apps concerned with localization.
 *
 * Usage:
 *
 * this.marketToasts.critical('Server was unable to complete your request', { persistent: true });
 */
export default class MarketToasts extends Service {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "marketToasterSelector", 'market-toaster');
    _defineProperty(this, "dismissText", 'Dismiss');
    _defineProperty(this, "activeToasts", new Map());
    _defineProperty(this, "nextToastId", 1);
  }
  _getToaster() {
    const toaster = document.querySelector(this.marketToasterSelector);
    assert(`[market-toasts] could not find an element for selector '${this.marketToasterSelector}'. You may need to add a 'market-toaster' to your application's template, or if one exists, make sure the configured selector matches the element.`, toaster);
    assert(`[market-toasts] the element specified by selector '${this.marketToasterSelector}' must match a 'market-toaster' element. Got ${toaster.tagName}`, toaster.tagName === 'MARKET-TOASTER');
    return toaster;
  }
  critical(message, options) {
    return this.show(message, MarketToastVariant.Critical, options ?? {});
  }
  info(message, options) {
    return this.show(message, MarketToastVariant.Info, options ?? {});
  }
  success(message, options) {
    return this.show(message, MarketToastVariant.Success, options ?? {});
  }
  warning(message, options) {
    return this.show(message, MarketToastVariant.Warning, options ?? {});
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appendAction(toast, action, actionText) {
    const toastButton = document.createElement('button');
    toastButton.slot = 'action';
    toastButton.innerText = actionText ?? '';
    toastButton.onclick = function (ev) {
      run(() => {
        action.call(this, ev);
      });
    };
    toast.appendChild(toastButton);
  }
  show(message, variant, {
    persistent = false,
    progress = -1,
    action,
    actionText,
    secondaryAction,
    secondaryActionText,
    htmlSafe
  }) {
    // create toast element
    const toast = document.createElement('market-toast');
    toast.variant = variant;
    toast.progress = progress;
    if (htmlSafe) {
      toast.innerHTML = message;
    } else {
      toast.appendChild(document.createTextNode(message));
    }
    if (persistent) {
      toast.persistent = true;
    }

    // dismiss button is required, but aria-label can be localized
    toast.dismissButtonAriaLabel = this.dismissText;

    // append optional actions if passed
    if (action) {
      this.appendAction(toast, action, actionText);
    }
    if (secondaryAction) {
      this.appendAction(toast, secondaryAction, secondaryActionText);
    }
    const toastId = this.nextToastId++;
    this.activeToasts.set(toastId, toast);
    let showFinished = false;
    this._getToaster().show(toast).finally(() => {
      showFinished = true;
    });
    Ember.testing && registerWaiter(() => showFinished);
    return toastId;
  }
  remove(toastId) {
    const toast = this.activeToasts.get(toastId);
    if (toast) {
      let exitAnimationComplete = false;
      this.activeToasts.delete(toastId);
      this._getToaster().hide(toast).finally(() => {
        exitAnimationComplete = true;
      });
      Ember.testing && registerWaiter(() => exitAnimationComplete);
    }
  }
  updateProgress(toastId, progress) {
    const toast = this.activeToasts.get(toastId);
    if (toast) {
      toast.progress = progress;
    }
  }
}